import { useRef, useState } from 'react';
import type { FC } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography,
} from '@material-ui/core';
import useAuth from '../../hooks/useAuth';
import CogIcon from '../../icons/Cog';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Logout } from '@material-ui/icons';

const AccountPopover: FC = () => {
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const { t } = useTranslation(['navBar']);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const getImage = useSelector((state: any) => state.userInfo?.userInfos);
  const imageSrc = (Object.keys(getImage).length < 1 ? user.image : getImage) || 'https://assets.it22.nl/fortes-energy/admin/dummy_image.png';

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const handleLogout = async (): Promise<void> => {
    try {
      handleClose();
      await logout();
      navigate('/login');
    } catch (err) {
      console.error(err);
      toast.error('Unable to logout.');
    }
  };

  return (
    <>
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
        }}
      >
        <Avatar
          src={imageSrc}
          sx={{
            height: 32,
            width: 32,
            color: '#718096'
          }}
        />
      </Box>
      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: '200px', padding: '4px 0' },
        }}
      >
        <Box>
          <MenuItem
            component={RouterLink}
            to="/account"
            sx={{
              px: 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: '#f7fafc',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 20, display: 'flex', alignItems: 'center' }}>
              <CogIcon sx={{ color: '#718096', fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                  fontSize='14px'
                  sx={{ fontWeight: 400 }}
                >
                  {t('navBar_profileDropdown_user_settings')}
                </Typography>
              )}
            />
          </MenuItem>
          <Divider variant='middle' />
          <MenuItem
            onClick={handleLogout}
            sx={{
              px: 2,
              py: 1.5,
              '&:hover': {
                backgroundColor: '#f7fafc',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 20, display: 'flex', alignItems: 'center' }}>
              <Logout fontSize='small' sx={{ color: '#EE3235', }} />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="#EE3235"
                  variant="subtitle2"
                  fontSize='14px'
                  sx={{ fontWeight: 400 }}
                >
                  {t('navBar_profileDropdown_logout')}
                </Typography>
              )}
            />
          </MenuItem>
        </Box>
      </Popover>
    </>
  );
};

export default AccountPopover;